import { BaseServiceLegacy } from "./base.service.legacy";
import {
  EmploymentFormValues,
  EmploymentRequest,
  EmploymentResponse,
  IJob,
  ISalaryData,
  ApiResponseOne,
  ApiResponseList,
  DraftEmploymentReadModel,
} from "../shared/types";
import {
  CollaboratorCoverageSummary,
  CollaboratorCoverageDetail,
} from "hvp-shared";
import dayjs from "dayjs";
import {
  WORKING_DAYS_IN_A_WEEK,
  MAX_WORK_WEEK_LIMIT,
  AVERAGE_WORK_DAYS_PER_MONTH,
} from "../shared";
import hvpAuthenticatedApi from "../api/hvpAuthenticatedApi";

interface EmploymentDraftResponse {
  prevEmployment: EmploymentResponse | null;
  newEmployment: EmploymentResponse;
}

const API_URL = "/employments";
export class EmploymentService extends BaseServiceLegacy<
  EmploymentResponse,
  EmploymentRequest,
  EmploymentFormValues
> {
  constructor() {
    super(API_URL);
  }

  public transformResponseToFormValues(
    response: EmploymentResponse
  ): EmploymentFormValues {
    if (!response) {
      throw new Error("Employment response is null or undefined");
    }
    return {
      ...response,
      employmentStartDate: dayjs(response.employmentStartDate),
      employmentEndDate: response.employmentEndDate
        ? dayjs(response.employmentEndDate)
        : undefined,
    };
  }

  public transformFormValuesToRequest(
    formValues: EmploymentFormValues
  ): EmploymentRequest {
    return {
      ...formValues,
      employmentStartDate: formValues.employmentStartDate.toISOString(),
      employmentEndDate: formValues.employmentEndDate?.toISOString() || null,
      createdAt:
        formValues.createdAt && new Date(formValues.createdAt).toISOString(),
      updatedAt:
        formValues.updatedAt && new Date(formValues.updatedAt).toISOString(),
    };
  }

  public async getDraft(
    collaboratorId: string,
    startDate: string
  ): Promise<ApiResponseOne<EmploymentDraftResponse>> {
    const params = {
      sort_by: "employmentStartDate",
      direction: "desc",
      collaboratorId,
      startDate,
    };

    const response = await hvpAuthenticatedApi.get<
      ApiResponseOne<EmploymentDraftResponse>
    >(`${this.API_URL}/draft/${collaboratorId}`, { params });
    return response.data;
  }

  private formatNumber(value: number): number {
    return Math.round(value * 100) / 100;
  }

  public recalculateEmployment({
    job,
    employment,
    salaryData,
  }: RecalculateEmploymentParams) {
    const { guaranteedJobIncome = 0, jobFixedIncome = 0 } = job;

    const {
      weeklyHours,
      employmentHourlyRate: currentHourlyRate,
      seniorityBonusPercentage,
    } = employment;

    // Basic calculations
    const dailyWorkingHours = weeklyHours / WORKING_DAYS_IN_A_WEEK;
    const workWeekRatio = weeklyHours / MAX_WORK_WEEK_LIMIT;

    // Calculate guaranteed income (preserve if current is higher)

    const employmentGuaranteedIncome = guaranteedJobIncome * workWeekRatio;

    // Calculate employment fixed income by job
    const employmentFixedIncomeByJob =
      jobFixedIncome * workWeekRatio * (1 + seniorityBonusPercentage);

    // Calculate total fixed income
    const totalFixedIncome = employmentFixedIncomeByJob;

    // Calculate nominal daily and hourly fixed income
    const nominalDailyFixedIncome = totalFixedIncome / 30;
    const nominalHourlyFixedIncome =
      nominalDailyFixedIncome / dailyWorkingHours;

    // Calculate effective daily and hourly income (at least minimum wage)
    const minimumWage = salaryData?.minimumWage || 0;
    const effectiveDailyFixedIncome = Math.max(
      totalFixedIncome / AVERAGE_WORK_DAYS_PER_MONTH,
      minimumWage
    );
    const effectiveHourlyFixedIncome =
      effectiveDailyFixedIncome / dailyWorkingHours;

    // Calculate employment hourly rate
    const calculatedHourlyRate =
      totalFixedIncome / AVERAGE_WORK_DAYS_PER_MONTH / dailyWorkingHours;
    const employmentHourlyRate = Math.max(
      calculatedHourlyRate,
      currentHourlyRate || 0
    );

    const result = {
      ...employment,
      // Calculated values
      dailyWorkingHours: this.formatNumber(dailyWorkingHours),
      workWeekRatio: this.formatNumber(workWeekRatio),
      employmentGuaranteedIncome: this.formatNumber(employmentGuaranteedIncome),
      employmentFixedIncomeByJob: this.formatNumber(employmentFixedIncomeByJob),
      totalFixedIncome: this.formatNumber(totalFixedIncome),
      nominalDailyFixedIncome: this.formatNumber(nominalDailyFixedIncome),
      nominalHourlyFixedIncome: this.formatNumber(nominalHourlyFixedIncome),
      employmentHourlyRate: this.formatNumber(employmentHourlyRate),
      effectiveDailyFixedIncome: this.formatNumber(effectiveDailyFixedIncome),
      effectiveHourlyFixedIncome: this.formatNumber(effectiveHourlyFixedIncome),
    };

    return result;
  }

  public async getPreviousEmployment(collaboratorId: string) {
    const employments = await this.getAll({
      collaboratorId,
      sort_by: "employmentStartDate",
      direction: "desc",
    });
    const employment = employments.data[0];
    if (!employment) return null;
    return this.transformResponseToFormValues(employment);
  }

  public async getBulkDrafts(
    startDate: string,
    endDate?: string
  ): Promise<ApiResponseList<DraftEmploymentReadModel>> {
    const params: any = { startDate };
    if (endDate) {
      params.endDate = endDate;
    }

    const response = await hvpAuthenticatedApi.get<
      ApiResponseList<DraftEmploymentReadModel>
    >(`${this.API_URL}/draft`, { params });
    return response.data;
  }

  public async bulkSave(
    employments: EmploymentRequest[]
  ): Promise<EmploymentResponse[]> {
    const response = await hvpAuthenticatedApi.post<EmploymentResponse[]>(
      `${this.API_URL}/bulk`,
      employments
    );
    return response.data;
  }

  public async recalculateEmploymentFromAPI(
    employment: EmploymentFormValues
  ): Promise<EmploymentFormValues> {
    const employmentRequest = this.transformFormValuesToRequest(employment);
    const response = await hvpAuthenticatedApi.post<
      ApiResponseOne<EmploymentResponse>
    >(`${this.API_URL}/recalculate`, employmentRequest);
    return this.transformResponseToFormValues(response.data.data);
  }

  public async recalculateBulkEmploymentsFromAPI(
    employments: EmploymentFormValues[]
  ): Promise<EmploymentFormValues[]> {
    const employmentRequests = employments.map((employment) =>
      this.transformFormValuesToRequest(employment)
    );

    const response = await hvpAuthenticatedApi.post<
      ApiResponseList<EmploymentResponse>
    >(`${this.API_URL}/recalculate-bulk`, employmentRequests);

    return response.data.data.map((employment) =>
      this.transformResponseToFormValues(employment)
    );
  }

  public async calculateAverageHours(
    employmentId: string
  ): Promise<{
    averageDailyHoursWorked: number;
    totalHours: number;
    calendarDays: number;
    windowStartDate: string;
    windowEndDate: string;
    isFallback: boolean;
  }> {
    const response = await hvpAuthenticatedApi.post<{
      success: boolean;
      resource: {
        averageDailyHoursWorked: number;
        totalHours: number;
        calendarDays: number;
        windowStartDate: string;
        windowEndDate: string;
        isFallback: boolean;
      };
    }>(`${this.API_URL}/${employmentId}/calculate-average-hours`);
    return response.data.resource;
  }

  // --- SBC/SDI Recalculation (GH#13 SP11) ---

  public async previewAllSbc(): Promise<BulkSbcResult> {
    const response = await hvpAuthenticatedApi.get<{
      success: boolean;
      resource: BulkSbcResult;
    }>(`${this.API_URL}/preview-all-sbc`);
    return response.data.resource;
  }

  public async previewSbc(employmentId: string): Promise<SbcResult> {
    const response = await hvpAuthenticatedApi.get<{
      success: boolean;
      resource: SbcResult;
    }>(`${this.API_URL}/${employmentId}/sbc`);
    return response.data.resource;
  }

  public async calculateSbc(input: {
    collaboratorId: string;
    employmentStartDate: string;
    employmentGuaranteedIncome: number;
  }): Promise<SbcResult> {
    const response = await hvpAuthenticatedApi.post<{
      success: boolean;
      resource: SbcResult;
    }>(`${this.API_URL}/calculate-sbc`, input);
    return response.data.resource;
  }

  public async recalculateSbc(
    employmentId: string
  ): Promise<SbcResult & { previousSbc: number }> {
    const response = await hvpAuthenticatedApi.patch<{
      success: boolean;
      resource: SbcResult & { previousSbc: number };
    }>(`${this.API_URL}/${employmentId}/recalculate-sbc`);
    return response.data.resource;
  }

  public async recalculateAllSbc(): Promise<BulkSbcResult> {
    const response = await hvpAuthenticatedApi.post<{
      success: boolean;
      resource: BulkSbcResult;
    }>(`${this.API_URL}/recalculate-all-sbc`);
    return response.data.resource;
  }

  public async getCoverageSummary(): Promise<CollaboratorCoverageSummary[]> {
    const response = await hvpAuthenticatedApi.get<
      ApiResponseList<CollaboratorCoverageSummary>
    >(`${this.API_URL}/coverage-summary`);
    return response.data.data;
  }

  public async getCoverageDetail(
    collaboratorId: string
  ): Promise<CollaboratorCoverageDetail> {
    const response = await hvpAuthenticatedApi.get<
      ApiResponseOne<CollaboratorCoverageDetail>
    >(`${this.API_URL}/coverage/${collaboratorId}`);
    return response.data.data;
  }
}

type RecalculateEmploymentParams = {
  employment: EmploymentFormValues;
  job: IJob;
  salaryData?: ISalaryData | null;
};

interface SbcResult {
  sbc: number;
  dailySalary: number;
  factor: number;
  quincenasUsed: number;
  totalPerceptions: number;
}

interface BulkSbcResult {
  updated: number;
  skipped: number;
  errors: { employmentId: string; error: string }[];
  details: {
    employmentId: string;
    collaboratorName: string;
    previousSbc: number;
    newSbc: number;
    diff: number;
  }[];
}
